import type { ErrorHandler, TypedSocket } from '@context/socket.context';
import useAuth from '@hooks/use-auth';
import useDocumentTitle from '@hooks/use-document-title';
import useSocket from '@hooks/use-socket';
import ChatPicker from '@pages/ChatFeed/components/ChatPicker';
import LeftColumn from '@pages/ChatFeed/LeftColumn';
import RightColumn from '@pages/ChatFeed/RightColumn';
import { loadMessageTemplates } from '@utils/api/load-message-templates';
import buildClassName from '@utils/build-class-name';
import { Modal } from 'antd';
import type { FC } from 'react';
import { memo, useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import { useBoundStore } from '@/newstore';
import { useTotalUnread } from '@/newstore/selectors/conversations';
import { useActiveConversation, useIsRightColumnShown, useSharedActions } from '@/newstore/selectors/shared';

import styles from './ChatFeed.module.scss';

interface StateProps {
  socket: TypedSocket;
  unread: number;
  isLeftColumnOpen: boolean;
  isMiddleColumnOpen: boolean;
  isRightColumnOpen: boolean;
}

const NonMemoChatFeed: FC<StateProps> = ({ socket, unread, isLeftColumnOpen, isRightColumnOpen }) => {
  // const isSmallScreen = useMediaQuery('(max-width: 768px)');
  const [modal, contextHolder] = Modal.useModal();

  useDocumentTitle(unread > 0 ? `(${unread})` : undefined);

  useEffect(() => {
    const errorHandler: ErrorHandler = (exception) => {
      modal.error({
        type: 'error',
        title: 'Error',
        content: exception.message,
      });
    };

    socket.on('exception', errorHandler);

    return () => {
      socket.off('exception', errorHandler);
    };
  }, [modal, socket]);

  const classname = buildClassName(
    styles.chatFeed,
    isLeftColumnOpen && styles.leftColumnOpen,
    isRightColumnOpen && styles.rightColumnOpen,
    isRightColumnOpen && 'right-column-shown', // TODO: with transitions this will be refactored
  );

  return (
    <div id="chat-feed" className={classname}>
      <LeftColumn />
      {/* Middle Column */}
      <Outlet />
      <RightColumn />
      <ChatPicker title="Forward to..." onlyAuthorized={true} />
      {contextHolder}
    </div>
  );
};

const ChatFeed = memo(function ChatFeed() {
  const isLeftColumnShown = useBoundStore((state) => state.isLeftColumnShown);
  const isRightColumnShown = useIsRightColumnShown();
  const socket = useSocket();
  const auth = useAuth();
  const userId = auth.getUserId();
  const totalUnread = useTotalUnread();
  const { conversation } = useActiveConversation();
  const { fetchActiveConversationMembers } = useSharedActions();

  useEffect(() => {
    if (userId === undefined) {
      return;
    }

    // noinspection JSIgnoredPromiseFromCall
    loadMessageTemplates(auth);
  }, [userId, auth]);

  useEffect(() => {
    const controller = new AbortController();

    fetchActiveConversationMembers(auth, controller.signal);

    return () => {
      controller.abort();
    };
  }, [auth, conversation?.id, fetchActiveConversationMembers]);

  return (
    <NonMemoChatFeed
      socket={socket}
      unread={totalUnread}
      isLeftColumnOpen={isLeftColumnShown}
      isMiddleColumnOpen={true}
      isRightColumnOpen={isRightColumnShown}
    />
  );
});

export default ChatFeed;
